import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Sparkles, User, Ruler, Scale, Activity, Heart, ChevronRight, ChevronLeft, Check, Loader2, Globe, FileText, CheckCircle2 } from "lucide-react";
import { calculateMetrics } from "./onboarding/calculations";
import {
  PersonalInfoStep, BodyAndMeasurementsStep,
  HealthHistoryStep, LifestyleGoalsStep
} from "./onboarding/Steps";
import MedicationAlertModal from "@/components/MedicationAlertModal";

// Feb 2026 — consolidated: Body (2 fields) + Measurements (3 fields)
// merged into a single "Тяло и измервания" step (5 fields on one screen).
// This reduces onboarding friction and matches the "single source of truth"
// pattern used by BodyMetricsEditModal on the dashboard / doctor profile.
const STEPS = [
  { id: "personal",   icon: User },
  { id: "body_meas",  icon: Ruler },
  { id: "health",     icon: Heart },
  { id: "lifestyle",  icon: Activity },
  { id: "report",     icon: FileText },
];

// Wizard form field → backend progressive-save key. See PROGRESSIVE_FIELDS
// in /app/backend/routes/profile.py for the authoritative whitelist.
const SAVE_KEY_MAP = {
  fullName: "fullName",
  age: "age",
  gender: "gender",
  height: "height",
  weight: "weight",
  neckCircumference: "neck",
  waistCircumference: "waist",
  hipCircumference: "hip",
  familyHistory: "familyHistory",
  currentConditions: "currentConditions",
  medications: "medications",
  activityLevel: "activityLevel",
  sleepHours: "sleepHours",
  stressLevel: "stressLevel",
  dietType: "dietType",
  primaryGoal: "primaryGoal",
};

const LANG_OPTIONS = [
  { code: "bg", label: "BG" },
  { code: "en", label: "EN" },
  { code: "es", label: "ES" },
];

export default function OnboardingWizard({ onComplete }) {
  const { API, user, refreshUser } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportLang, setReportLang] = useState(language);
  const [savedMetrics, setSavedMetrics] = useState(null);
  const [showMedAlert, setShowMedAlert] = useState(false);
  const [matchedDrugs, setMatchedDrugs] = useState([]);
  // "idle" | "saving" | "saved" — drives the small "Запазено ✓" indicator
  const [autoSaveState, setAutoSaveState] = useState("idle");
  const saveTimeoutsRef = useRef({});  // per-field debounce timers
  const pendingSaveRef = useRef({});   // fields queued but not yet sent

  const stepTitles = [
    bg ? "Лична инф." : "Personal",
    bg ? "Тяло и измервания" : "Body & measurements",
    bg ? "Здраве" : "Health",
    bg ? "Начин на живот" : "Lifestyle",
    bg ? "AI Доклад" : "AI Report",
  ];

  const [formData, setFormData] = useState({
    fullName: "", age: "", gender: "male",
    height: "", weight: "",
    neckCircumference: "", waistCircumference: "", hipCircumference: "",
    familyHistory: "", currentConditions: "", medications: "",
    activityLevel: "moderate", sleepHours: "7", stressLevel: "moderate",
    dietType: "mixed", primaryGoal: "general_health"
  });

  // Prefill form from existing user.profile data on mount. Users who returned
  // to /onboarding (e.g. closed the tab at step 3) see their saved progress.
  useEffect(() => {
    const p = user?.profile;
    if (!p) return;
    setFormData(prev => ({
      ...prev,
      fullName: p.personal?.fullName || prev.fullName,
      age: p.personal?.age ? String(p.personal.age) : prev.age,
      gender: p.personal?.gender || prev.gender,
      height: p.body?.height ? String(p.body.height) : prev.height,
      weight: p.body?.weight ? String(p.body.weight) : prev.weight,
      neckCircumference: p.measurements?.neck ? String(p.measurements.neck) : prev.neckCircumference,
      waistCircumference: p.measurements?.waist ? String(p.measurements.waist) : prev.waistCircumference,
      hipCircumference: p.measurements?.hip ? String(p.measurements.hip) : prev.hipCircumference,
      familyHistory: p.health?.familyHistory || prev.familyHistory,
      currentConditions: p.health?.currentConditions || prev.currentConditions,
      medications: p.health?.medications || prev.medications,
      activityLevel: p.lifestyle?.activityLevel || prev.activityLevel,
      sleepHours: p.lifestyle?.sleepHours || prev.sleepHours,
      stressLevel: p.lifestyle?.stressLevel || prev.stressLevel,
      dietType: p.lifestyle?.dietType || prev.dietType,
      primaryGoal: p.lifestyle?.primaryGoal || prev.primaryGoal,
    }));
  }, [user]);

  // Flush any pending save immediately (used before step navigation / submit)
  const flushPendingSaves = useCallback(async () => {
    Object.values(saveTimeoutsRef.current).forEach(clearTimeout);
    saveTimeoutsRef.current = {};
    const payload = { ...pendingSaveRef.current };
    pendingSaveRef.current = {};
    if (Object.keys(payload).length === 0) return;
    setAutoSaveState("saving");
    try {
      await axios.patch(`${API}/user/profile/progressive`, payload);
      setAutoSaveState("saved");
      setTimeout(() => setAutoSaveState("idle"), 1200);
    } catch {
      setAutoSaveState("idle");
    }
  }, [API]);

  // Queue a field for backend save with per-field debounce. Radio/select
  // values come in as final strings and only fire once, so 500ms is fine.
  // Free-text inputs get the same treatment — if user keeps typing, the
  // timer resets.
  const scheduleSave = useCallback((field, value) => {
    const saveKey = SAVE_KEY_MAP[field];
    if (!saveKey) return;
    if (value === "" || value === null || value === undefined) return;
    pendingSaveRef.current[saveKey] = value;
    if (saveTimeoutsRef.current[field]) clearTimeout(saveTimeoutsRef.current[field]);
    saveTimeoutsRef.current[field] = setTimeout(async () => {
      const payload = { ...pendingSaveRef.current };
      pendingSaveRef.current = {};
      if (Object.keys(payload).length === 0) return;
      setAutoSaveState("saving");
      try {
        await axios.patch(`${API}/user/profile/progressive`, payload);
        setAutoSaveState("saved");
        setTimeout(() => setAutoSaveState(s => s === "saved" ? "idle" : s), 1200);
      } catch {
        setAutoSaveState("idle");
      }
    }, 500);
  }, [API]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    scheduleSave(field, value);
  };

  // Auto-resume: on first mount with prefilled data, jump to the first
  // incomplete step so returning users never re-fill completed sections.
  const didResumeRef = useRef(false);
  useEffect(() => {
    if (didResumeRef.current) return;
    if (!user?.profile) return;
    const p = user.profile;
    const hasPersonal = !!(p.personal?.fullName && p.personal?.age && p.personal?.gender);
    const hasBody = !!(p.body?.height && p.body?.weight);
    const isMale = p.personal?.gender === "male";
    const hasMeas = !!(p.measurements?.neck && p.measurements?.waist && (isMale || p.measurements?.hip));
    let target = 0;
    if (hasPersonal) target = 1;
    if (hasPersonal && hasBody) target = 2;
    if (hasPersonal && hasBody && hasMeas) target = 4;  // skip optional Health step
    if (target > 0) {
      setCurrentStep(target);
      didResumeRef.current = true;
    }
  }, [user]);

  const generateReport = async (lang) => {
    setReportLoading(true);
    try {
      const metrics = savedMetrics || calculateMetrics(formData);
      if (!savedMetrics) setSavedMetrics(metrics);

      const { data } = await axios.post(`${API}/user/health-report`, {
        language: lang,
        metrics,
        formData,
      });
      setReportData(data);
      // Feb 2026 — Notify dashboard's LastAiReportCard to refetch immediately.
      try {
        window.dispatchEvent(new CustomEvent("nutramilo:ai-report-generated", { detail: data }));
      } catch (_) { /* legacy browsers */ }
    } catch (e) {
      toast.error(bg ? "Грешка при генериране на доклад" : "Error generating report");
    } finally {
      setReportLoading(false);
    }
  };

  const handleGoToReport = async () => {
    // Save profile first, then generate report
    setLoading(true);
    try {
      const metrics = calculateMetrics(formData);
      setSavedMetrics(metrics);
      // Feb 2026 — Detect "rerun onboarding" mode so backend can tag the
      // primary_goal audit log entry with source="onboarding_rerun". This
      // separates first-run from subsequent goal-strategy changes in the
      // HealthJourneyCard timeline.
      const params = new URLSearchParams(window.location.search);
      const isRerun = params.get("force") === "1" || params.get("rerun") === "1";
      const profileData = {
        ...formData, metrics,
        completedOnboarding: true,
        onboardingDate: new Date().toISOString(),
        __source: isRerun ? "onboarding_rerun" : "onboarding_initial",
      };
      await axios.post(`${API}/user/profile`, profileData);
      await refreshUser();

      // Check medications for drug-food interactions
      if (formData.medications && formData.medications.trim()) {
        try {
          const { data } = await axios.post(`${API}/drug-interactions/check-profile`, {
            medications: formData.medications,
          });
          if (data.matched && data.matched.length > 0) {
            setMatchedDrugs(data.matched);
            setShowMedAlert(true);
          }
        } catch (_) { /* non-blocking */ }
      }

      setCurrentStep(4); // Go to report step (index 4 after Feb 2026 consolidation to 5 steps)
      // Generate report
      await generateReport(reportLang);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save profile");
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    const metrics = savedMetrics || calculateMetrics(formData);
    onComplete({ ...formData, metrics, completedOnboarding: true });
  };

  const handleLangChange = async (newLang) => {
    setReportLang(newLang);
    await generateReport(newLang);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return formData.fullName && formData.age && formData.gender;
      case 1: {
        const isMale = formData.gender === "male";
        return formData.height && formData.weight
          && formData.neckCircumference && formData.waistCircumference
          && (isMale || formData.hipCircumference);
      }
      case 2: return true;
      case 3: return formData.activityLevel && formData.primaryGoal;
      case 4: return true;
      default: return false;
    }
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  const renderStep = () => {
    const props = { formData, updateField, bg };
    switch (currentStep) {
      case 0: return <PersonalInfoStep {...props} />;
      case 1: return <BodyAndMeasurementsStep {...props} />;
      case 2: return <HealthHistoryStep {...props} />;
      case 3: return <LifestyleGoalsStep {...props} />;
      case 4: return (
        <ReportStep
          reportData={reportData}
          reportLoading={reportLoading}
          reportLang={reportLang}
          onLangChange={handleLangChange}
          savedMetrics={savedMetrics}
          isDark={isDark}
          bg={bg}
        />
      );
      default: return null;
    }
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-background' : 'bg-gradient-to-br from-[#F0F9FF] via-[#FAFBFC] to-[#F0FDF4]'} py-6 sm:py-8 px-3 sm:px-4`}>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-2xl gradient-primary mb-3 sm:mb-4 shadow-lg shadow-sky-500/20">
            <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
          </div>
          <h1 className={`text-xl sm:text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>
            {currentStep === 4
              ? (bg ? "Персонализиран AI здравен доклад" : "Personalized AI Health Report")
              : (bg ? "Персонализирана здравна оценка" : "Personalized Health Assessment")}
          </h1>
          <p className={`text-sm sm:text-base ${isDark ? 'text-gray-400' : 'text-[#64748B]'}`}>
            {currentStep === 4
              ? (bg ? "AI анализира вашите данни, заболявания и медикаменти" : "AI analyzes your data, conditions, and medications")
              : (bg ? "Помогнете ни да ви опознаем за персонализирани препоръки" : "Help us understand you for personalized insights")}
          </p>
          {/* iter592 — Task 7: продуктовата история в onboarding (само първата стъпка) */}
          {currentStep === 0 && (
            <p className={`mt-2 text-[11px] ${isDark ? "text-gray-500" : "text-gray-400"}`} data-testid="onboarding-story-line">
              {bg
                ? "Събираме данни → разбираме организма ви → изграждаме вашия дигитален здравен близнак → даваме персонализирани действия → проследяваме резултатите."
                : "We collect data → understand your body → build your digital health twin → give personalised actions → track the results."}
            </p>
          )}
          {autoSaveState !== "idle" && currentStep < 4 && (
            <p className={`mt-2 text-[11px] font-medium inline-flex items-center gap-1.5 ${autoSaveState === "saved" ? "text-emerald-500" : isDark ? "text-gray-500" : "text-gray-400"}`} data-testid="onboarding-autosave">
              {autoSaveState === "saving" ? (
                <><Loader2 className="w-3 h-3 animate-spin" /> {bg ? "Запазване..." : "Saving..."}</>
              ) : (
                <><CheckCircle2 className="w-3 h-3" /> {bg ? "Запазено" : "Saved"}</>
              )}
            </p>
          )}
        </div>

        {/* Progress — iter1099: минималистичен text „Стъпка X от N" за
            да не се дублира с външния RegisterProgressStepper при signup
            flow-a. Тънък linear bar под текста остава като визуална ръка. */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-baseline justify-between mb-1.5 leading-tight">
            <span
              data-testid="wizard-step-label"
              className={`text-xs font-semibold ${isDark ? "text-slate-300" : "text-[#0EA5E9]"}`}
            >
              {bg
                ? `Стъпка ${currentStep + 1} от ${STEPS.length}`
                : `Step ${currentStep + 1} of ${STEPS.length}`}
            </span>
            <span
              className={`text-[11px] ${isDark ? "text-slate-500" : "text-[#94A3B8]"}`}
            >
              {stepTitles[currentStep]}
            </span>
          </div>
          <Progress value={progress} className="h-1.5" />
        </div>

        {/* Form Card */}
        <Card className={isDark ? 'bg-card border-border' : 'card-elevated border-0'}>
          <CardContent className="p-4 sm:p-6">
            {renderStep()}

            {/* Navigation Buttons — Feb 2026: 5-step consolidation.
                Step 4 = AI Report step (previously step 5). */}
            <div className="flex justify-between mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-[#E2E8F0]">
              {currentStep < 4 ? (
                <>
                  <Button variant="ghost" onClick={async () => { await flushPendingSaves(); setCurrentStep(prev => prev - 1); }} disabled={currentStep === 0} className={`h-11 ${isDark ? 'text-gray-400' : 'text-[#64748B]'}`}>
                    <ChevronLeft className="w-4 h-4 mr-1 sm:mr-2" />
                    {bg ? "Назад" : "Back"}
                  </Button>
                  {currentStep < 3 ? (
                    <Button onClick={async () => { await flushPendingSaves(); setCurrentStep(prev => prev + 1); }} disabled={!canProceed()} className="gradient-primary text-white rounded-xl px-4 sm:px-6 h-11" data-testid="onboarding-next-btn">
                      {bg ? "Напред" : "Next"}
                      <ChevronRight className="w-4 h-4 ml-1 sm:ml-2" />
                    </Button>
                  ) : (
                    <Button onClick={async () => { await flushPendingSaves(); await handleGoToReport(); }} disabled={!canProceed() || loading} className="gradient-primary text-white rounded-xl px-4 sm:px-6 h-11" data-testid="onboarding-generate-report-btn">
                      {loading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{bg ? "Анализиране..." : "Analyzing..."}</>
                      ) : (
                        <><Sparkles className="w-4 h-4 mr-2" />{bg ? "Генерирай AI доклад" : "Generate AI Report"}</>
                      )}
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <div />
                  <Button onClick={handleFinish} className="gradient-primary text-white rounded-xl px-6 h-11" data-testid="finish-onboarding-btn">
                    <Check className="w-4 h-4 mr-2" />
                    {bg ? "Към платформата" : "Go to Platform"}
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Disclaimer */}
        <p className={`mt-6 text-center text-xs max-w-md mx-auto ${isDark ? 'text-gray-500' : 'text-[#94A3B8]'}`} data-testid="onboarding-disclaimer">
          {bg
            ? "Данните ви се използват само за персонализиране на вашето изживяване. Това не е медицинска оценка."
            : language === "es"
            ? "Sus datos se utilizan solo para personalizar su experiencia. Esto no es una evaluacion medica."
            : "Your data is used only to personalize your experience. This is not a medical assessment."}
        </p>
      </div>

      <MedicationAlertModal
        open={showMedAlert}
        onClose={() => setShowMedAlert(false)}
        matchedDrugs={matchedDrugs}
      />
    </div>
  );
}

function ReportStep({ reportData, reportLoading, reportLang, onLangChange, savedMetrics, isDark, bg }) {
  if (reportLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16" data-testid="report-loading">
        <Loader2 className="w-10 h-10 text-[#0EA5E9] animate-spin mb-4" />
        <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>
          {bg ? "AI анализира вашите данни..." : "AI is analyzing your data..."}
        </p>
        <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-[#94A3B8]'}`}>
          {bg ? "Включва заболявания, медикаменти и фамилна обремененост" : "Including conditions, medications, and family history"}
        </p>
      </div>
    );
  }

  if (!reportData) return null;

  const m = savedMetrics || reportData.standard_metrics || {};

  return (
    <div data-testid="report-step">
      {/* Language Selector */}
      <div className="flex items-center justify-center gap-2 mb-5" data-testid="report-lang-selector">
        <Globe className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
        {LANG_OPTIONS.map(opt => (
          <button
            key={opt.code}
            onClick={() => onLangChange(opt.code)}
            disabled={reportLoading}
            data-testid={`report-lang-${opt.code}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              reportLang === opt.code
                ? "bg-[#0EA5E9] text-white shadow-md"
                : isDark ? "bg-[hsl(223,47%,14%)] text-gray-400 hover:text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Standard Metrics Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
        {[
          { label: "BMR", value: `${m.bmr} kcal`, color: "#0EA5E9" },
          { label: "TDEE", value: `${m.tdee} kcal`, color: "#10B981" },
          { label: "BMI", value: m.bmi, color: "#F59E0B" },
          { label: bg ? "Мазнини" : "Body Fat", value: `${m.bodyFat}%`, color: "#8B5CF6" },
        ].map(item => (
          <div key={item.label} className={`p-3 rounded-xl text-center ${isDark ? 'bg-[hsl(223,47%,12%)]' : 'bg-gray-50'}`}>
            <p className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{item.label}</p>
            <p className="text-lg font-bold" style={{ color: item.color }}>{item.value}</p>
          </div>
        ))}
      </div>

      {/* AI Report */}
      <div className={`p-4 rounded-xl border ${isDark ? 'bg-[hsl(223,47%,8%)] border-[hsl(223,47%,15%)]' : 'bg-white border-gray-100'}`}>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-[#0EA5E9]" />
          <h3 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>
            {bg ? "AI персонализиран анализ" : "AI Personalized Analysis"}
          </h3>
        </div>
        <div className={`text-sm leading-relaxed whitespace-pre-line ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-testid="ai-report-text">
          {reportData.report}
        </div>
      </div>
    </div>
  );
}
